<template>
  <div class="chiller-usage-card" :class="{ 'usage-disabled': fieldsDisabled }">
    <!-- Collapsible Header -->
    <div
      class="chiller-usage-header pointer"
      :class="{
        'usage-error': hasError && isCollapsed,
        'usage-disabled-text': fieldsDisabled
      }"
      @click="onCollapseClick"
    >
      <div class="usage-header-left">
        <v-checkbox
          :model-value="isUsed"
          :disabled="disabled"
          @update:model-value="onChecked"
          hide-details
          density="compact"
          class="usage-checkbox"
          @click.stop
        />

        <div>
          <div class="usage-title">
            {{ label }}
          </div>

          <div class="usage-subtitle">
            {{ isUsed ? 'Aktiv' : 'Nicht aktiv' }}
          </div>
        </div>
      </div>

      <v-icon class="usage-chevron">
        {{ isCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
      </v-icon>
    </div>

    <!-- Collapsible Input Area -->
    <div v-show="!isCollapsed" class="chiller-usage-body">
      <!-- Usage Profile -->
      <v-autocomplete
        v-model="usageProfile"
        :items="usageProfiles"
        item-title="name"
        item-value="id"
        :disabled="fieldsDisabled"
        :error-messages="filteredFieldErrors.usage_profile"
        hide-details="auto"
        density="comfortable"
        label="Nutzungsprofil"
        @update:model-value="touch('usage_profile')"
      >
        <template #append>
          <v-tooltip v-if="hint.usage_profile" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.usage_profile }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Usage Share -->
      <v-text-field
        v-model.number="usageShare"
        :disabled="fieldsDisabled"
        :error-messages="filteredFieldErrors.usage_share"
        hide-details="auto"
        density="comfortable"
        label="Anteil der Nutzungsart"
        suffix="%"
        :hint="hint.usage_share"
        @update:model-value="touch('usage_share')"
      />

      <!-- Operation Mode -->
      <v-autocomplete
        v-model="operationMode"
        :items="operationModes"
        item-title="text"
        item-value="value"
        :disabled="fieldsDisabled || isAC"
        :error-messages="filteredFieldErrors.operation_mode"
        hide-details="auto"
        density="comfortable"
        label="Betriebsart"
        @update:model-value="touch('operation_mode')"
      >
        <template #append>
          <v-tooltip v-if="hint.operation_mode" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.operation_mode }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Humidity Requirement -->
      <v-autocomplete
        v-if="isAHU"
        v-model="humidityRequirement"
        :items="humidityRequirements"
        item-title="text"
        item-value="value"
        :disabled="fieldsDisabled"
        :error-messages="filteredFieldErrors.humidity_requirement"
        hide-details="auto"
        density="comfortable"
        label="Feuchteanforderung"
        no-data-text="Betriebsart wählen"
        @update:model-value="touch('humidity_requirement')"
      >
        <template #append>
          <v-tooltip v-if="hint.humidity_requirement" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.humidity_requirement }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Heat Recovery -->
      <v-autocomplete
        v-if="isAHU"
        v-model="heatRecovery"
        :items="heatRecoveries"
        item-title="text"
        item-value="value"
        :disabled="fieldsDisabled"
        :error-messages="filteredFieldErrors.heat_recovery"
        hide-details="auto"
        density="comfortable"
        label="Wärmerückgewinnung"
        no-data-text="Feuchteanforderung wählen"
        @update:model-value="touch('heat_recovery')"
      >
        <template #append>
          <v-tooltip v-if="hint.heat_recovery" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.heat_recovery }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- Temperature free cooling -->
      <v-autocomplete
        v-if="freeCooling"
        v-model="temperatureFreeCooling"
        :items="temperaturesFreeCooling"
        item-title="text"
        item-value="value"
        :disabled="fieldsDisabled"
        :error-messages="filteredFieldErrors.temperature_free_cooling"
        hide-details="auto"
        density="comfortable"
        label="Temperaturniveau freie Kühlung"
        suffix="°C"
        no-data-text="Nutzungsprofil wählen"
        @update:model-value="touch('temperature_free_cooling')"
      >
        <template #append>
          <v-tooltip v-if="hint.temperature_free_cooling" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.temperature_free_cooling }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import api from '@/plugins/axios'
import { djangoChoicesToVuetify } from '@/utils'

/* ---------------- PROPS ---------------- */
const props = defineProps({
  modelValue: { required: true },              // v-model for nested object
  fieldErrors: { type: Object, default: () => ({}) },
  chillerType: { required: true },
  freeCooling: { required: true },
  knownSeer: { required: true },
  label: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  initiallyChecked: { type: Boolean, default: false },
  hint: { type: Object, default: () => ({}) }
})

/* ---------------- EMITS ---------------- */
const emit = defineEmits(['update:modelValue'])

/* ---------------- LOCAL STATE ---------------- */
const isChecked = ref(false)
const isCollapsed = ref(false)
const touchedFields = ref([])

/* Field values */
const usageProfile = ref(null)
const usageShare = ref(null)
const operationMode = ref(null)
const humidityRequirement = ref(null)
const heatRecovery = ref(null)
const temperatureFreeCooling = ref(null)

/* Options loaded via API */
const usageProfiles = ref([])
const operationModes = ref([])
const humidityRequirements = ref([])
const heatRecoveries = ref([])
const temperaturesFreeCooling = ref([])

/* ---------------- COMPUTED ---------------- */
const isUsed = computed(() => !props.disabled && isChecked.value)

const isAC = computed(() => props.chillerType === 3)
const isAHU = computed(() => operationMode.value === 2)
const fieldsDisabled = computed(() => !isUsed.value || props.disabled)

const hasError = computed(() =>
  Object.keys(props.fieldErrors || {}).length > 0 && isUsed.value
)

/* Convert nested object <-> fields */
const dataObject = computed({
  get() {
    if (!isUsed.value) return null
    if (props.chillerType === 2) return null
    if (props.chillerType === 3 && props.knownSeer) return null

    const result = {
      usage_profile: usageProfile.value,
      usage_share: usageShare.value
    }

    if (isAHU.value) {
      result.humidity_requirement = humidityRequirement.value
      result.heat_recovery = heatRecovery.value
    }

    // operation mode rules
    if (!isAC.value) {
      result.operation_mode = operationMode.value
    }
    if (isAC.value && !props.knownSeer) {
      result.operation_mode = 1
    }

    if (props.freeCooling) {
      result.temperature_free_cooling = temperatureFreeCooling.value
    }

    return result
  },

  set(v) {
    if (!v) {
      isChecked.value = false
      return
    }

    isChecked.value = true
    usageProfile.value = v.usage_profile
    usageShare.value = v.usage_share
    operationMode.value = v.operation_mode
    humidityRequirement.value = v.humidity_requirement
    heatRecovery.value = v.heat_recovery
    temperatureFreeCooling.value = v.temperature_free_cooling
  }
})

/* Filter only touched error fields */
const filteredFieldErrors = computed(() => {
  const result = {}
  for (const key of touchedFields.value) {
    if (props.fieldErrors[key]) result[key] = props.fieldErrors[key]
  }
  return result
})

/* ---------------- WATCHERS ---------------- */

// Sync incoming v-model
watch(
  () => props.modelValue,
  (v) => {
    if (JSON.stringify(v) !== JSON.stringify(dataObject.value)) {
      dataObject.value = v
    } else if (!v) {
      dataObject.value = null
    }
  },
  { immediate: true }
)

// Emit nested object upward
watch(dataObject, (v) => emit('update:modelValue', v))

// Load dependent options
watch(operationMode, (v) => {
  if (v === 2) fetchHumidityRequirements()
}, { immediate: true })

watch(humidityRequirement, (v) => {
  if (v != null) fetchHeatRecoveries()
}, { immediate: true })

watch(() => props.chillerType, (v) => {
  if (v != null && v !== 2) fetchOperationModes()
}, { immediate: true })

watch(usageProfile, () => fetchTemperaturesFreeCooling(), { immediate: true })

/* ---------------- INIT ---------------- */
if (props.initiallyChecked) {
  isChecked.value = true
} else {
  isCollapsed.value = true
}

fetchUsageProfiles()

/* ---------------- METHODS ---------------- */
function onChecked(val) {
  isChecked.value = val
  if (!val) {
    dataObject.value = null
  }
}

function touch(field) {
  touchedFields.value.push(field)
  touchedFields.value = Array.from(new Set(touchedFields.value))
}

function onCollapseClick() {
  isCollapsed.value = !isCollapsed.value
  if (!isCollapsed.value) {
    for (const key of Object.keys(props.fieldErrors)) {
      touch(key)
    }
  }
}

/* ---------------- API FETCHERS (option A) ---------------- */
async function fetchUsageProfiles() {
  try {
    const { data } = await api.get('/methods/chiller/usage-profiles/')
    usageProfiles.value = data
  } catch {}
}

async function fetchOperationModes() {
  try {
    const { data } = await api.get('/methods/chiller/operation-modes/', {
      params: { chiller_type: props.chillerType }
    })
    operationModes.value = djangoChoicesToVuetify(data)
    if (operationModes.value.length === 1) {
      operationMode.value = operationModes.value[0].value
    }
  } catch {}
}

async function fetchHumidityRequirements() {
  try {
    const { data } = await api.get('/methods/chiller/humidity-requirements/', {
      params: { operation_mode: operationMode.value }
    })
    humidityRequirements.value = djangoChoicesToVuetify(data)
  } catch {}
}

async function fetchHeatRecoveries() {
  try {
    const { data } = await api.get('/methods/chiller/heat-recoveries/', {
      params: { humidity_requirement: humidityRequirement.value }
    })
    heatRecoveries.value = djangoChoicesToVuetify(data)
  } catch {}
}

async function fetchTemperaturesFreeCooling() {
  try {
    const { data } = await api.get('/methods/chiller/temperatures-free-cooling/', {
      params: { usage_profile: usageProfile.value }
    })
    temperaturesFreeCooling.value = djangoChoicesToVuetify(data)
  } catch {}
}
</script>

<style scoped lang="scss">
.chiller-usage-card {
  border-radius: 22px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  background-color: rgb(var(--v-theme-surface));
  overflow: hidden;
}

.chiller-usage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.9rem;
  background-color: rgba(var(--v-theme-primary), 0.035);
  transition:
    background-color 160ms ease,
    border-color 160ms ease;
}

.chiller-usage-header:hover {
  background-color: rgba(var(--v-theme-primary), 0.065);
}

.usage-header-left {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.usage-checkbox {
  flex-shrink: 0;
}

.usage-title {
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
  line-height: 1.25;
}

.usage-subtitle {
  margin-top: 0.1rem;
  font-size: 0.78rem;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.usage-chevron {
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
}

.chiller-usage-body {
  display: grid;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.chiller-usage-body :deep(.v-field) {
  border-radius: 18px;
}

.chiller-usage-body :deep(.v-input__append .v-icon),
.chiller-usage-body :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.usage-error {
  color: rgb(var(--v-theme-error));
  background-color: rgba(var(--v-theme-error), 0.08);
}

.usage-disabled {
  opacity: 0.72;
}

.usage-disabled-text {
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.pointer {
  cursor: pointer;
}

@media (max-width: 700px) {
  .chiller-usage-header {
    align-items: flex-start;
  }

  .chiller-usage-body {
    padding: 0.85rem;
  }
}
</style>